import {useCallback, useEffect, useRef, useState} from 'react';

import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Observer} from 'mobx-react';

import {useI18n} from '@yourcoach/i18n';

import type {MetricModel} from '../../../../../../core/domain/Metric/Metric.model';
import type {Ref as TextInputRef} from '../../../../../../uikit/TextInput/types';
import type {Result} from './types';

import MinusIcon from '../../../../../../assets/icons/minus.svg';
import AddIcon from '../../../../../../assets/icons/secondary/Add.svg';
import CheckIcon from '../../../../../../assets/icons/secondary/Tick Square.svg';
import {alert} from '../../../../../../components/alert';
import {Modal} from '../../../../../../components/Modal';
import {Clickable} from '../../../../../../uikit/Clickable';
import {Icon} from '../../../../../../uikit/Icon';
import {Text} from '../../../../../../uikit/Text';
import {logger} from '../../../../../../utils/logger';
import {useMobxStore} from '../../../../../../utils/store';
import {TestID} from '../../containers/Trackers/test_ids';
import {MotivationalCommentModal} from './components/MotivationalCommentModal';
import {WeightInput} from './components/WeightInput';
import {AddWeightStore} from './store';
import * as S from './styles';

type Props = {
  defaultUnit?: MetricModel.WeightUnit;
  defaultWeight?: number;
  onSubmit: (result: Result) => Promise<void>;
  onSubmitComment?: (text: string) => Promise<void>;
};

export const AddWeightModal = NiceModal.create<Props>(
  ({defaultUnit, defaultWeight, onSubmit, onSubmitComment}) => {
    const modal = useModal();
    const store = useMobxStore(
      () => new AddWeightStore({defaultUnit, defaultWeight}),
    );
    const {t} = useI18n();
    const [loading, setLoading] = useState(false);

    const inputRef = useRef<TextInputRef>(null);
    const onUnitClick = (value: MetricModel.WeightUnit) => {
      store.changeUnit(value);
      requestAnimationFrame(() => inputRef.current?.focus());
    };

    useEffect(() => {
      logger.event('add_weight_screen_view');
    }, []);

    const onSubmitClick = useCallback(async () => {
      logger.event('add_weight_save_tap');

      try {
        setLoading(true);
        await onSubmit(store.result);

        modal.hide();

        alert.show({
          autoHideAfterMs: 4000,
          confetti: true,
          icon: CheckIcon,
          iconColor: 'static.accent.trackers',
          iconSize: 72,
          isDismissible: false,
          renderMessage: () => (
            <S.Message>{t('shared:add_weight_consistency_message')}</S.Message>
          ),
          renderTitle: () => (
            <S.MessageTitle>
              {t('shared:thanks_for_checking_in')}
            </S.MessageTitle>
          ),
          variant: 'notification',
        });

        setTimeout(() => {
          alert.hide();

          if (onSubmitComment) {
            setTimeout(() => {
              NiceModal.show(MotivationalCommentModal, {onSubmitComment});
            }, 200);
          }
        }, 4000);
      } catch (e) {
        logger.error(e);

        alert.error(e);
      } finally {
        setLoading(false);
      }
    }, [modal, onSubmit, onSubmitComment, store.result, t]);

    return (
      <Modal
        isOpen={modal.visible}
        maxWidth="420px"
        onAfterClose={modal.remove}
        onRequestClose={modal.hide}
        title={t('shared:add_weight')}
      >
        <Observer>
          {() => (
            <S.Container>
              <S.WeightContainer>
                <Clickable
                  onClick={() => store.adjustWeight(-0.1)}
                  testID={TestID.button_decrease_weight}
                >
                  <S.IconContainer>
                    <Icon color="icon3" svg={MinusIcon} />
                  </S.IconContainer>
                </Clickable>
                <S.WeightInputContainer>
                  <WeightInput
                    inputRef={inputRef}
                    onRequestSubmit={onSubmitClick}
                    store={store}
                  />
                </S.WeightInputContainer>
                <Clickable
                  onClick={() => store.adjustWeight(0.1)}
                  testID={TestID.button_increase_weight}
                >
                  <S.IconContainer>
                    <Icon color="icon3" svg={AddIcon} />
                  </S.IconContainer>
                </Clickable>
              </S.WeightContainer>
              <S.OptionsContainer>
                {store.options.map(({isSelected, label, value}) => (
                  <S.UnitOption
                    isSelected={isSelected}
                    key={value}
                    onClick={() => onUnitClick(value)}
                  >
                    <Text
                      color={isSelected ? 'text.white' : 'text.black'}
                      style={{textTransform: 'capitalize'}}
                      variant="label.la"
                      weight="bold"
                    >
                      {label}
                    </Text>
                  </S.UnitOption>
                ))}
              </S.OptionsContainer>
              <S.SubmitButton
                disabled={!store.isValid}
                isLoading={loading}
                onClick={onSubmitClick}
                testID={TestID.button_submit_weight}
              >
                {t('shared:add_weight')}
              </S.SubmitButton>
            </S.Container>
          )}
        </Observer>
      </Modal>
    );
  },
);
